import React, { useMemo, useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  CardContent,
  IconButton,
  TextField,
  Typography,
  Stack,
  useTheme,
  Tooltip,
  Button,
  MenuItem,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import AddCommentOutlinedIcon from "@mui/icons-material/AddCommentOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { useDispatch, useSelector } from "react-redux";
import GlassContainer from "../../../components/liquid-glass";
import { RootState } from "../../../../../redux/store";
import { normalizeUsersForAI } from "./normalize";
import {
  setMiniChatDraft,
  addMiniChatMessage,
  setMiniChatMessages,
  replaceLastMiniChatMessage,
  clearMiniChat,
  type MiniChatMessage,
} from "../../../../../redux/slices/partnersSlice";
import {
  CognitoUserPool,
  CognitoUserSession,
} from "amazon-cognito-identity-js";
import { Meal, User, Workout } from "../../../../../models/types";

// --- CUSTOM MARKDOWN RENDERER ---
const MarkdownRenderer = ({ content }: { content: string }) => {
  const lines = content.split('\n');
  const blocks: React.ReactNode[] = [];
  let listBuffer: React.ReactNode[] = [];

  const parseInline = (text: string) => {
    const parts = text.split(/(\*\*.*?\*\*|\*.*?\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      }
      if (part.startsWith('*') && part.endsWith('*')) {
        return <em key={i}>{part.slice(1, -1)}</em>;
      }
      return part;
    });
  };

  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (trimmed.startsWith('###')) {
      if (listBuffer.length) {
        blocks.push(<ul key={`list-${index}`} style={{ margin: '0 0 8px 0', paddingLeft: '24px' }}>{listBuffer}</ul>);
        listBuffer = [];
      }
      blocks.push(
        <Typography key={`h-${index}`} variant="subtitle1" sx={{ fontWeight: 700, mt: 1.5, mb: 0.5 }}>
          {parseInline(trimmed.replace(/^###\s*/, ''))}
        </Typography>
      );
    } else if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      listBuffer.push(
        <li key={`li-${index}`} style={{ marginBottom: '4px' }}>
          <Typography variant="body1" component="span">
            {parseInline(trimmed.substring(2))}
          </Typography>
        </li>
      );
    } else {
      if (listBuffer.length) {
        blocks.push(<ul key={`list-${index}`} style={{ margin: '0 0 8px 0', paddingLeft: '24px' }}>{listBuffer}</ul>);
        listBuffer = [];
      }
      if (trimmed.length > 0) {
        blocks.push(
          <Typography key={`p-${index}`} variant="body1" sx={{ mb: 1.5, lineHeight: 1.6 }}>
            {parseInline(line)}
          </Typography>
        );
      }
    }
  });

  if (listBuffer.length) {
    blocks.push(<ul key="list-end" style={{ margin: '0 0 8px 0', paddingLeft: '24px' }}>{listBuffer}</ul>);
  }

  return <Box>{blocks}</Box>;
};

// --- Helpers (unchanged) ---
const today = () => new Date();
const daysAgo = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  d.setHours(0, 0, 0, 0);
  return d;
};
const inRange = (dateISO?: string, start?: Date, end?: Date) => {
  if (!dateISO) return false;
  const d = new Date(dateISO);
  return (!start || d >= start) && (!end || d <= end);
};
const workoutsPerWeek = (workouts: Workout[], windowDays: number = 14) => {
  const start = daysAgo(windowDays - 1);
  const end = today();
  const count = (workouts ?? []).filter((w) =>
    inRange(w.dayOfExecution, start, end)
  ).length;
  return (count / windowDays) * 7;
};
type DayAgg = { calories: number; protein: number; hasMeals: boolean };
const mealAggLastNDays = (meals: Meal[] | undefined, nDays = 7) => {
  const start = daysAgo(nDays - 1);
  const agg: Record<string, DayAgg> = {};
  for (let i = 0; i < nDays; i++) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const key = d.toISOString().slice(0, 10);
    agg[key] = { calories: 0, protein: 0, hasMeals: false };
  }
  (meals ?? []).forEach((m) => {
    if (!inRange(m.dateTime, daysAgo(nDays - 1), today())) return;
    const key = new Date(m.dateTime).toISOString().slice(0, 10);
    if (!agg[key]) agg[key] = { calories: 0, protein: 0, hasMeals: false };
    agg[key].calories += m.calories ?? 0;
    agg[key].protein += m.protein ?? 0;
    agg[key].hasMeals = true;
  });
  return agg;
};
const trackingConsistencyPct = (meals: Meal[] | undefined, nDays = 7) => {
  const agg = mealAggLastNDays(meals, nDays);
  const vals = Object.values(agg);
  const tracked = vals.filter((d) => d.hasMeals).length;
  return (tracked / nDays) * 100;
};
const goalMissRate = (user: User, nDays = 7) => {
  const agg = mealAggLastNDays(user.meals, nDays);
  const useCalories =
    user.goals?.trainingGoal === "weight loss" ||
    user.goals?.trainingGoal === "maintain";
  const basis: "calories" | "protein" = useCalories ? "calories" : "protein";
  let tracked = 0,
    misses = 0,
    hits = 0;
  for (const d of Object.values(agg)) {
    if (!d.hasMeals) continue;
    tracked++;
    if (basis === "calories") {
      const goal = user.goals?.caloriesGoal ?? 0;
      if (goal <= 0) {
        tracked--;
        continue;
      }
      const lower = goal * 0.9;
      const upper = goal * 1.1;
      const hit = d.calories >= lower && d.calories <= upper;
      if (!hit) misses++;
      else hits++;
    } else {
      const goal = user.goals?.proteinGoal ?? 0;
      if (goal <= 0) {
        tracked--;
        continue;
      }
      const hit = d.protein >= goal;
      if (!hit) misses++;
      else hits++;
    }
  }
  return {
    missPct: tracked > 0 ? (misses / tracked) * 100 : 100,
    basis,
    trackedDays: tracked,
    daysHit: hits,
  };
};

const poolData = {
  UserPoolId: "us-east-2_nECr2VdPc",
  ClientId: "6jd4v12t5keoj2eupcrjkcia2c",
};
const userPool = new CognitoUserPool(poolData);

const CHAT_HISTORY_STORAGE_KEY = "avro-mini-chat-sessions-v1";
const INITIAL_ASSISTANT_GREETING =
  "Hey there Coach!\nAsk me anything about your clients.";
const INITIAL_GREETING_MESSAGE: MiniChatMessage = {
  role: "assistant",
  content: INITIAL_ASSISTANT_GREETING,
};

type ChatSession = {
  id: string;
  title: string;
  createdAt: number;
  updatedAt: number;
  messages: MiniChatMessage[];
};

const createSessionId = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const deriveSessionTitle = (messages: MiniChatMessage[]) => {
  const firstUser = messages.find(
    (m) => m.role === "user" && m.content.trim().length > 0
  );
  if (!firstUser) return "New chat";
  const compact = firstUser.content.replace(/\s+/g, " ").trim();
  return compact.length > 48 ? `${compact.slice(0, 48)}...` : compact;
};

const createEmptySession = (): ChatSession => {
  const now = Date.now();
  return {
    id: createSessionId(),
    title: "New chat",
    createdAt: now,
    updatedAt: now,
    messages: [INITIAL_GREETING_MESSAGE],
  };
};

const sanitizeMessages = (value: unknown): MiniChatMessage[] => {
  if (!Array.isArray(value)) return [INITIAL_GREETING_MESSAGE];
  const parsed = value
    .filter((item): item is MiniChatMessage => {
      if (!item || typeof item !== "object") return false;
      const role = (item as MiniChatMessage).role;
      const content = (item as MiniChatMessage).content;
      return (
        (role === "assistant" || role === "user") && typeof content === "string"
      );
    })
    .map((item) => ({
      role: item.role,
      content: item.content,
    }));
  return parsed.length ? parsed : [INITIAL_GREETING_MESSAGE];
};

const sanitizeSessions = (value: unknown): ChatSession[] => {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const id = (item as ChatSession).id;
      if (typeof id !== "string" || !id.trim()) return null;

      const createdAtRaw = (item as ChatSession).createdAt;
      const updatedAtRaw = (item as ChatSession).updatedAt;
      const createdAt =
        typeof createdAtRaw === "number" && Number.isFinite(createdAtRaw)
          ? createdAtRaw
          : Date.now();
      const updatedAt =
        typeof updatedAtRaw === "number" && Number.isFinite(updatedAtRaw)
          ? updatedAtRaw
          : createdAt;
      const messages = sanitizeMessages((item as ChatSession).messages);
      const titleRaw = (item as ChatSession).title;

      return {
        id,
        createdAt,
        updatedAt,
        messages,
        title:
          typeof titleRaw === "string" && titleRaw.trim()
            ? titleRaw.trim()
            : deriveSessionTitle(messages),
      } satisfies ChatSession;
    })
    .filter((session): session is ChatSession => Boolean(session))
    .sort((a, b) => b.updatedAt - a.updatedAt);
};

const areMessagesEqual = (a: MiniChatMessage[], b: MiniChatMessage[]) => {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i].role !== b[i].role || a[i].content !== b[i].content) {
      return false;
    }
  }
  return true;
};

export default function MiniChat() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const glassBg =
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 100%)";

  const clients =
    (useSelector(
      (state: RootState) => state.partners.clientsWithWorkouts
    ) as unknown as User[]) ?? [];

  const darkMode = useSelector((state: RootState) => state.partners.darkMode);
  const messages = useSelector((s: RootState) => s.partners.miniChat.messages);
  const draft = useSelector((s: RootState) => s.partners.miniChat.draft);
  const partnerUserId =
    useSelector(
      (s: RootState) => s.partners.partnerData?.partnerData?.userId
    ) ?? "partner";

  // Local State
  const [suggestedOptions, setSuggestedOptions] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [chatSessions, setChatSessions] = useState<ChatSession[]>([]);
  const [activeSessionId, setActiveSessionId] = useState<string>("");
  const sessionsHydratedRef = useRef(false);
  const skipNextSessionSyncRef = useRef(false);

  const sessionStorageKey = useMemo(
    () => `${CHAT_HISTORY_STORAGE_KEY}:${partnerUserId}`,
    [partnerUserId]
  );

  const orderedSessions = useMemo(
    () => [...chatSessions].sort((a, b) => b.updatedAt - a.updatedAt),
    [chatSessions]
  );
  const selectedSessionId = activeSessionId || orderedSessions[0]?.id || "";

  const aiContexts = useMemo(() => {
    if (!clients.length) return [];
    return normalizeUsersForAI(clients);
  }, [clients]);

  const chatContextSummary = useMemo(() => {
    const numClients = clients.length;
    const totalWorkoutsLast7 = clients.reduce((acc: number, u: User) => {
      const n = (u.workouts ?? []).filter((w) =>
        inRange(w.dayOfExecution, daysAgo(6), today())
      ).length;
      return acc + n;
    }, 0);
    const pctOnTrackDiet = (() => {
      if (!clients.length) return 0;
      const onTrackCount = clients.reduce((acc: number, u: User) => {
        const { trackedDays, daysHit } = goalMissRate(u, 7);
        if (trackedDays === 0) return acc;
        return acc + (daysHit >= 4 ? 1 : 0);
      }, 0);
      return (onTrackCount / clients.length) * 100;
    })();

    return `Clients: ${numClients}, 7d workouts: ${totalWorkoutsLast7}, On-track diet: ${pctOnTrackDiet.toFixed(0)}%.`;
  }, [clients]);

  const [sending, setSending] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const typewriterTimerRef = useRef<number | null>(null);

  const [idToken, setIdToken] = useState<string>("");
  const [isFetchingToken, setIsFetchingToken] = useState<boolean>(true);

  useEffect(() => {
    setIsFetchingToken(true);
    const currentUser = userPool.getCurrentUser();
    if (!currentUser) {
      setIsFetchingToken(false);
      return;
    }
    currentUser.getSession((err: any, session: CognitoUserSession | null) => {
      if (err) {
        setIsFetchingToken(false);
        return;
      }
      if (session && session.isValid()) {
        setIdToken(session.getIdToken().getJwtToken());
      }
      setIsFetchingToken(false);
    });
  }, []);

  const LAMBDA_URL = "https://m01op8z79h.execute-api.us-east-2.amazonaws.com/default/avro-coach-ai";

  useEffect(() => {
    if (typeof window === "undefined") return;
    sessionsHydratedRef.current = false;

    let loadedSessions: ChatSession[] = [];
    try {
      const raw = window.localStorage.getItem(sessionStorageKey);
      if (raw) {
        loadedSessions = sanitizeSessions(JSON.parse(raw));
      }
    } catch (err) {
      console.error("Failed to load mini-chat history from local storage", err);
    }

    if (!loadedSessions.length) {
      loadedSessions = [createEmptySession()];
    }

    const firstSession = loadedSessions[0];
    setChatSessions(loadedSessions);
    setActiveSessionId(firstSession.id);
    setSuggestedOptions([]);
    skipNextSessionSyncRef.current = true;
    dispatch(setMiniChatMessages(firstSession.messages));
    dispatch(setMiniChatDraft(""));

    sessionsHydratedRef.current = true;
  }, [dispatch, sessionStorageKey]);

  useEffect(() => {
    if (!sessionsHydratedRef.current || typeof window === "undefined") return;
    try {
      window.localStorage.setItem(sessionStorageKey, JSON.stringify(chatSessions));
    } catch (err) {
      console.error("Failed to persist mini-chat history to local storage", err);
    }
  }, [chatSessions, sessionStorageKey]);

  useEffect(() => {
    if (!sessionsHydratedRef.current || !activeSessionId || isTyping) return;
    if (skipNextSessionSyncRef.current) {
      skipNextSessionSyncRef.current = false;
      return;
    }

    setChatSessions((prev) => {
      const currentIndex = prev.findIndex((s) => s.id === activeSessionId);
      if (currentIndex < 0) return prev;

      const currentSession = prev[currentIndex];
      if (areMessagesEqual(currentSession.messages, messages)) {
        return prev;
      }

      const nextMessages = messages.length ? messages : [INITIAL_GREETING_MESSAGE];
      const updatedSession: ChatSession = {
        ...currentSession,
        messages: nextMessages,
        title: deriveSessionTitle(nextMessages),
        updatedAt: Date.now(),
      };

      const others = prev.filter((s) => s.id !== activeSessionId);
      return [updatedSession, ...others];
    });
  }, [activeSessionId, isTyping, messages]);

  const trimContexts = (arr: string[], maxChars = 200000) =>
    arr.map((c) => (c.length > maxChars ? c.slice(0, maxChars) + " …" : c));

  const callAI = async ({
    messages,
    latestUserText,
  }: {
    messages: MiniChatMessage[];
    latestUserText: string;
  }) => {
    const payload = {
      model: "gemma-3-27b-it",
      contexts: trimContexts(aiContexts),
      opsSummary: chatContextSummary,
      messages,
      prompt: latestUserText,
    };

    const resp = await fetch(LAMBDA_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(idToken ? { Authorization: idToken } : {}),
      },
      body: JSON.stringify(payload),
    });

    if (!resp.ok) {
      const text = await resp.text().catch(() => "");
      throw new Error(`AI error ${resp.status}: ${text || "Unknown error"}`);
    }

    const data = await resp.json();
    return data;
  };

  const send = async (overrideText?: string) => {
    const text = overrideText || draft.trim();
    if (!text || sending || isTyping) return;

    dispatch(setMiniChatDraft(""));
    setSuggestedOptions([]);
    dispatch(addMiniChatMessage({ role: "user", content: text }));

    try {
      setSending(true);
      const aiResponse = await callAI({
        messages,
        latestUserText: text,
      });

      const replyText = aiResponse.text || "No response text.";
      const newOptions = Array.isArray(aiResponse.options) ? aiResponse.options : [];

      setIsTyping(true);
      dispatch(addMiniChatMessage({ role: "assistant", content: "" }));

      const full = replyText;
      let idx = 0;

      if (typewriterTimerRef.current) {
        window.clearInterval(typewriterTimerRef.current);
        typewriterTimerRef.current = null;
      }

      typewriterTimerRef.current = window.setInterval(() => {
        idx = Math.min(idx + 1, full.length);
        dispatch(
          replaceLastMiniChatMessage({
            role: "assistant",
            content: full.slice(0, idx),
          })
        );
        if (idx >= full.length) {
          if (typewriterTimerRef.current) {
            window.clearInterval(typewriterTimerRef.current);
            typewriterTimerRef.current = null;
          }
          setIsTyping(false);
          // Set options locally, they will be rendered inside the bubble
          if (newOptions.length > 0) {
            setSuggestedOptions(newOptions);
            setTimeout(autoScrollToBottom, 100);
          }
        }
      }, 14) as any;
    } catch (err: any) {
      console.error(err);
      setIsTyping(false);
      dispatch(
        addMiniChatMessage({
          role: "assistant",
          content:
            "Sorry—there was a problem talking to Avro. Please try again.",
        })
      );
    } finally {
      setSending(false);
    }
  };

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const autoScrollToBottom = () => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  };
  useEffect(() => { autoScrollToBottom(); }, []);
  useEffect(() => {
    const id = requestAnimationFrame(autoScrollToBottom);
    return () => cancelAnimationFrame(id);
  }, [messages, sending, isTyping, suggestedOptions]);

  const stopTypingAnimation = useCallback(() => {
    if (typewriterTimerRef.current) {
      window.clearInterval(typewriterTimerRef.current);
      typewriterTimerRef.current = null;
    }
    setIsTyping(false);
  }, []);

  const onSelectSession = useCallback(
    (sessionId: string) => {
      const session = orderedSessions.find((item) => item.id === sessionId);
      if (!session) return;
      stopTypingAnimation();
      setSuggestedOptions([]);
      setActiveSessionId(sessionId);
      skipNextSessionSyncRef.current = true;
      dispatch(setMiniChatMessages(session.messages));
      dispatch(setMiniChatDraft(""));
    },
    [dispatch, orderedSessions, stopTypingAnimation]
  );

  const onCreateNewChat = useCallback(() => {
    stopTypingAnimation();
    setSuggestedOptions([]);
    const freshSession = createEmptySession();
    setChatSessions((prev) => [freshSession, ...prev]);
    setActiveSessionId(freshSession.id);
    skipNextSessionSyncRef.current = true;
    dispatch(clearMiniChat());
  }, [dispatch, stopTypingAnimation]);

  return (
    <Box
      sx={{
        position: "relative",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ px: 2, pt: 1, pb: 0.5, display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          select
          size="small"
          value={selectedSessionId}
          onChange={(e) => onSelectSession(e.target.value)}
          disabled={!orderedSessions.length}
          sx={{
            minWidth: 180,
            flex: 1,
            "& .MuiOutlinedInput-root": { borderRadius: 2 },
          }}
        >
          {orderedSessions.map((session) => (
            <MenuItem key={session.id} value={session.id}>
              {session.title}
            </MenuItem>
          ))}
        </TextField>

        <Tooltip title="New chat">
          <span>
            <IconButton onClick={onCreateNewChat} disabled={sending || isTyping} color="primary">
              <AddCommentOutlinedIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {/* MESSAGES AREA */}
      <CardContent
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          pt: 1.5,
          pb: 2,
          background: "transparent",
        }}
      >
        <Stack gap={1.2}>
          {messages.map((m, i) => {
            const isLastMessage = i === messages.length - 1;
            const showOptions = isLastMessage && m.role === 'assistant' && suggestedOptions.length > 0 && !isTyping && !sending;

            return (
              <Box
                key={i}
                sx={{
                  alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                  px: 2,
                  py: 1.5,
                  borderRadius: 2,
                  bgcolor: m.role === "user" ? "primary.main" : "transparent",
                  color: m.role === "user" ? "primary.contrastText" : darkMode ? "primary.contrastText" : "text.primary",
                  maxWidth: "90%",
                  fontSize: "0.95rem",
                  boxShadow: m.role === "assistant" && darkMode ? 'inset 0 0 10px rgba(255,255,255,0.05)' : 'none',
                }}
              >
                {m.role === "user" ? (
                  <Typography variant="body1">{m.content}</Typography>
                ) : (
                  <Stack gap={1}>
                    <MarkdownRenderer content={m.content} />

                    {/* EMBEDDED OPTIONS BLOCK (Styled as Cards) */}
                    {showOptions && (
                      <Box sx={{ mt: 2, pt: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Suggested Actions
                        </Typography>
                        <Stack spacing={1}>
                          {suggestedOptions.map((opt, idx) => (
                            <Button
                              key={idx}
                              variant="outlined"
                              onClick={() => send(opt)}
                              startIcon={<CheckBoxOutlineBlankIcon sx={{ color: 'text.secondary', fontSize: 20 }} />}
                              sx={{
                                justifyContent: "flex-start",
                                textAlign: "left",
                                p: 1.5,
                                borderRadius: 2,
                                textTransform: "none",
                                bgcolor: darkMode ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                                borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)',
                                color: darkMode ? "text.primary" : "text.primary",
                                lineHeight: 1.4,
                                '&:hover': {
                                  bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
                                  borderColor: 'primary.main',
                                }
                              }}
                            >
                              {opt}
                            </Button>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                )}
              </Box>
            );
          })}

          {sending && !isTyping && (
            <Typography
              variant="body2"
              sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 1 }}
            >
              <Box
                component="span"
                sx={{
                  fontWeight: 800,
                  background: darkMode
                    ? "linear-gradient(130deg, #f4f4f6 0%, #9ea2ad 42%, #ffffff 72%, #8f95a1 100%)"
                    : "linear-gradient(130deg, #1f2733 0%, #7b8491 42%, #f5f7fa 72%, #4b5563 100%)",
                  backgroundSize: "220% 220%",
                  backgroundPosition: "0% 50%",
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                  WebkitTextFillColor: "transparent",
                  letterSpacing: 0.2,
                  animation: "thinking-shimmer 1.8s linear infinite",
                  "@keyframes thinking-shimmer": {
                    "0%": { backgroundPosition: "0% 50%" },
                    "100%": { backgroundPosition: "200% 50%" },
                  },
                }}
              >
                Thinking
              </Box>
              <Box component="span" sx={{ color: darkMode ? "primary.contrastText" : "text.secondary" }}>
                ...
              </Box>
            </Typography>
          )}
        </Stack>
      </CardContent>

      {/* INPUT AREA */}
      <Box sx={{ position: "sticky", bottom: 0, left: 0, right: 0, mx: 1, mb: 0.5, zIndex: 10 }}>
        <GlassContainer sx={{ background: glassBg }}>
          <Box sx={{ p: 1, display: "flex", gap: 1, width: "100%", alignItems: "center" }}>
            <TextField
              inputRef={inputRef}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root": {
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
              }}
              size="small"
              placeholder={suggestedOptions.length > 0 ? "Or type 'Other' here..." : "Ask Avro..."}
              value={draft}
              onChange={(e) => dispatch(setMiniChatDraft(e.target.value))}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  if (idToken && !isFetchingToken) send();
                }
              }}
              disabled={sending || isTyping || !idToken || isFetchingToken}
            />

            <Tooltip title="Send">
              <span>
                <IconButton color="primary" onClick={() => send()} disabled={sending || isTyping || !idToken || isFetchingToken}>
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        </GlassContainer>
      </Box>
    </Box>
  );
}
